import {
    getComparator,
    TableHeadCustom,
    TablePaginationCustom,
    TableSkeleton,
    useTable,
} from "../../../components/table";
import {useLocales} from "../../../locales";
import {useSnackbar} from "notistack";
import {useEffect, useMemo, useState} from "react";
import {Card, Dialog, Table, TableBody, TableContainer, Typography} from "@mui/material";
import CarriersToolbar from "../../../pages/Carriers/CarriersToolbar";
import OperatorLoginTableRow from "./OperatorLoginTableRow";
import {useAuthContext} from "../../../auth/useAuthContext";
import {useNavigate} from "react-router-dom";
import {PATH_DASHBOARD} from "../../../routes/path";
import {useSelector} from "react-redux";
import {dispatch} from "../../../redux/store";
import {fetchAllSubOperators} from "../../../redux/slices/operatorsBe";

const TABLE_HEAD = (t) => [
    {id: "name", label: t("carrierName"), align: "left"},
    {id: "country.name", label: t("country"), align: "left"},
    {id: "operatorDetails.description", label: t("description"), align: "left"},
    {id: "operatorDetails.status", label: t("status"), align: "left"},
    {id: "", width: 10, sortable: false},
];

export default function ChangeBusOperatorDialog({open, onClose}) {
    const {changeOperator} = useAuthContext();
    const navigate = useNavigate();
    const {enqueueSnackbar} = useSnackbar();
    const {translate} = useLocales();
    const {allSubOperators, loadingOperatorsBe} = useSelector(state => state.operatorsBE);

    const {
        dense,
        page,
        order,
        orderBy,
        rowsPerPage,
        onSort,
        onChangeDense,
        onChangePage,
        onChangeRowsPerPage,
        setPage,
        setSelectedOne,
    } = useTable({defaultOrderBy: "name"});

    const [filterName, setFilterName] = useState("");
    const [loading, setLoading] = useState(false);
    const isFiltered = filterName !== "";

    useEffect(() => {
        if (open) {
            dispatch(fetchAllSubOperators());
        }
    }, [open]);

    const dataFiltered = useMemo(() => {
        let data = allSubOperators ?? [];

        if (filterName?.trim()) {
            const q = filterName.toLowerCase();
            data = data.filter((x) => x?.name?.toLowerCase?.().includes(q));
        }

        const comparator = getComparator(order, orderBy);
        const safeComparator = (a, b) => {
            try {
                return comparator(a, b);
            } catch {
                return 0;
            }
        }
        return [...data].sort(safeComparator);
    }, [allSubOperators, filterName, order, orderBy]);

    const handleFilterName = (event) => {
        setPage(0);
        setFilterName(event.target.value);
    };

    const handleResetFilter = () => {
        setFilterName("");
        setSelectedOne(null);
    };

    const handleLogin = async (row) => {
        try {
            setLoading(true);
            await changeOperator(row.firebaseUid);
            navigate(PATH_DASHBOARD.bus.list);
            onClose();
        } catch (e) {
            enqueueSnackbar(translate("error"), {variant: "error"});
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
            <Card>
                <Typography sx={{p: 2}} variant="h6">
                    Pronađi operatera
                </Typography>

                <CarriersToolbar
                    showStatus={false}
                    isFiltered={isFiltered}
                    filterName={filterName}
                    onFilterName={handleFilterName}
                    onResetFilter={handleResetFilter}
                />

                <TableContainer sx={{maxHeight: 600, position: "relative", overflow: "auto"}}>
                    <Table size={dense ? "small" : "medium"} sx={{minWidth: 960}}>
                        <TableHeadCustom
                            order={order}
                            orderBy={orderBy}
                            headLabel={TABLE_HEAD(translate)}
                            rowCount={dataFiltered?.length || 0}
                            onSort={onSort}
                        />

                        <TableBody>
                            {loadingOperatorsBe
                                ? Array.from({length: rowsPerPage}, (_, i) => <TableSkeleton key={i} columns={5}/>)
                                : dataFiltered
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((row, idx) => (
                                        <OperatorLoginTableRow
                                            key={row?.id ?? idx}
                                            row={row}
                                            loading={loading}
                                            onLoginClick={() => handleLogin(row)}
                                        />
                                    ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePaginationCustom
                    loading={loadingOperatorsBe}
                    count={dataFiltered?.length || 0}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={onChangePage}
                    onRowsPerPageChange={onChangeRowsPerPage}
                    dense={dense}
                    onChangeDense={onChangeDense}
                />
            </Card>
        </Dialog>
    );
}
